import { createLogger } from '@/utils/logging';

import { KnowledgeCard, type KnowledgeSourceType } from './knowledge-card.model';

const logger = createLogger('KnowledgeCardMapper', 'mapper');

/* ------------------------------------------------------------------ */
/*  DTOs — transport-layer shapes matching the backend JSON contracts  */
/* ------------------------------------------------------------------ */

export interface KnowledgeCardDTO {
  card_id: string;
  client_id: string;
  project_id?: string | null;
  source_type: string;
  title: string;
  source_url?: string | null;
  summary_text?: string | null;
  preview_text?: string | null;
  ingestion_status: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface KnowledgeCardListDTO {
  items: KnowledgeCardDTO[];
  total: number;
}

/* ------------------------------------------------------------------ */
/*  Mappers                                                            */
/* ------------------------------------------------------------------ */

const KNOWN_SOURCE_TYPES: ReadonlySet<string> = new Set<KnowledgeSourceType>([
  'note',
  'document',
  'file_url',
  'markdown_import',
]);

/** Runtime guard: validates `raw` against the KnowledgeSourceType union.
 *  Falls back to 'file_url' and logs a warning when the API returns an unknown value. */
const toKnowledgeSourceType = (raw: string): KnowledgeSourceType => {
  if (KNOWN_SOURCE_TYPES.has(raw)) {
    return raw as KnowledgeSourceType;
  }
  logger.warn(`Unknown source_type "${raw}" from API; defaulting to "file_url"`, {});
  return 'file_url';
};

/** Map a single KnowledgeCardDTO from the API into a KnowledgeCard domain object. */
export const mapKnowledgeCardDTO = (dto: KnowledgeCardDTO): KnowledgeCard => {
  return new KnowledgeCard(
    dto.card_id,
    dto.client_id,
    dto.project_id ?? null,
    toKnowledgeSourceType(dto.source_type),
    dto.title,
    dto.source_url ?? null,
    dto.summary_text ?? null,
    dto.preview_text ?? null,
    dto.ingestion_status,
    dto.tags,
    dto.created_at,
    dto.updated_at,
  );
};

/** Map an array of KnowledgeCardDTOs into KnowledgeCard domain objects. */
export const mapKnowledgeCardListDTO = (dtos: KnowledgeCardDTO[]): KnowledgeCard[] => {
  return dtos.map(mapKnowledgeCardDTO);
};
